"use client"

import type { ReactNode } from "react"
import clsx from "clsx"
import { NODE_HEADING, NODE_PARAGRAPH } from "storyblok-rich-text-react-renderer"

import RichText from "~/components/ui/RichText"
import type { LegalsContentPropsSerialized } from "~/app/[locale]/legals/[slug]/_components/LegalsContent/_data/serializer"

import { sprinkles } from "~/styles/sprinkles.css"
import { text } from "~/styles/variants"

import * as css from "./styles.css"

export type LegalsContentProps = NonNullable<LegalsContentPropsSerialized>

type TitleHierarchy = "h1" | "h2" | "h3" | "h4" | "h5" | "h6"

export function LegalsContent({ textContent }: LegalsContentProps) {
  const transform = "uppercase"
  const weight = "medium"
  const lineHeight = 1.45

  const headingStyles: Record<TitleHierarchy, string> = {
    h1: text({ design: "spezia-24", transform, weight, lineHeight }),
    h2: text({ design: "spezia-24", transform, weight, lineHeight }),
    h3: text({ design: "riposte-14", transform, weight, lineHeight }),
    h4: text({ design: "riposte-14", transform, weight, lineHeight }),
    h5: text({ design: "riposte-14", transform, weight, lineHeight }),
    h6: text({ design: "riposte-14", transform, weight, lineHeight }),
  }

  return (
    <div
      className={clsx(
        sprinkles({
          pX: { mobile: 16, tablet: 120 },
          pT: { mobile: 20, tablet: 60 },
          pB: { mobile: 70, tablet: 60 },
        }),
        css.LegalsContent
      )}
    >
      <RichText
        nodeResolverOptions={{
          [NODE_HEADING]: (children, { level }) => {
            const Tag = `h${level}` as const
            const style = headingStyles?.[Tag]

            return <Tag className={clsx(style)}>{children}</Tag>
          },
          [NODE_PARAGRAPH]: (children: ReactNode) => {
            const result =
              children?.toString() !== undefined ? (
                <p className={text({ design: "riposte-14", lineHeight })}>{children}</p>
              ) : (
                <br /> ?? children
              )
            return result
          },
        }}
        render={textContent}
      />
    </div>
  )
}
